import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
  DragIndicator as DragIcon,
  Work as WorkIcon,
  Checklist as ChecklistIcon,
} from '@mui/icons-material';
import { onboardingApi } from '../services/api';
import {
  OnboardingChecklistItem,
  OnboardingChecklistItemCreate,
  OnboardingChecklistItemUpdate,
  PositionType,
  PositionTypeCreate,
  PositionTypeUpdate,
} from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`onboarding-tabpanel-${index}`}
      aria-labelledby={`onboarding-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const OnboardingConfig: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Position Type state
  const [positionDialogOpen, setPositionDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<PositionType | null>(null);
  const [showInactivePositions, setShowInactivePositions] = useState(false);
  const [positionFormData, setPositionFormData] = useState<PositionTypeCreate>({
    name: '',
    description: '',
    is_active: true,
  });

  // Checklist Item state
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<OnboardingChecklistItem | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [positionFilter, setPositionFilter] = useState<number | 'all' | 'universal'>('all');
  const [error, setError] = useState('');

  // Checklist form state
  const [formData, setFormData] = useState<OnboardingChecklistItemCreate>({
    name: '',
    description: '',
    category: '',
    position_type_id: undefined,
    sort_order: 0,
    is_required: false,
    default_days: undefined,
    is_active: true,
  });

  // Queries
  const { data: positionTypesData, isLoading: positionTypesLoading } = useQuery(
    ['position-types', showInactivePositions],
    () => onboardingApi.getPositionTypes({ include_inactive: showInactivePositions }),
    { keepPreviousData: true }
  );

  const { data: checklistData, isLoading: checklistLoading } = useQuery(
    ['onboarding-checklist', showInactive, positionFilter],
    () => onboardingApi.getChecklistItems({
      include_inactive: showInactive,
      position_type_id: positionFilter === 'all' || positionFilter === 'universal' ? undefined : positionFilter,
    }),
    { keepPreviousData: true }
  );

  // Position Type Mutations
  const createPositionMutation = useMutation(onboardingApi.createPositionType, {
    onSuccess: () => {
      queryClient.invalidateQueries(['position-types']);
      queryClient.invalidateQueries(['onboarding-checklist']);
      handleClosePositionDialog();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create position type');
    },
  });

  const updatePositionMutation = useMutation(
    ({ id, data }: { id: number; data: PositionTypeUpdate }) =>
      onboardingApi.updatePositionType(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['position-types']);
        queryClient.invalidateQueries(['onboarding-checklist']);
        handleClosePositionDialog();
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to update position type');
      },
    }
  );

  const deletePositionMutation = useMutation(onboardingApi.deletePositionType, {
    onSuccess: () => {
      queryClient.invalidateQueries(['position-types']);
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to delete position type');
    },
  });

  // Checklist Item Mutations
  const createChecklistMutation = useMutation(onboardingApi.createChecklistItem, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding-checklist']);
      handleCloseChecklistDialog();
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to create item');
    },
  });

  const updateChecklistMutation = useMutation(
    ({ id, data }: { id: number; data: OnboardingChecklistItemUpdate }) =>
      onboardingApi.updateChecklistItem(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['onboarding-checklist']);
        handleCloseChecklistDialog();
      },
      onError: (err: any) => {
        setError(err.response?.data?.detail || 'Failed to update item');
      },
    }
  );

  const deleteChecklistMutation = useMutation(onboardingApi.deleteChecklistItem, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding-checklist']);
    },
    onError: (err: any) => {
      setError(err.response?.data?.detail || 'Failed to delete item');
    },
  });

  // Position Type handlers
  const handleClosePositionDialog = () => {
    setPositionDialogOpen(false);
    setEditingPosition(null);
    setPositionFormData({
      name: '',
      description: '',
      is_active: true,
    });
    setError('');
  };

  const handleEditPosition = (position: PositionType) => {
    setEditingPosition(position);
    setPositionFormData({
      name: position.name,
      description: position.description || '',
      is_active: position.is_active,
    });
    setPositionDialogOpen(true);
  };

  const handleSubmitPosition = () => {
    if (!positionFormData.name) {
      setError('Name is required');
      return;
    }

    if (editingPosition) {
      updatePositionMutation.mutate({ id: editingPosition.id, data: positionFormData });
    } else {
      createPositionMutation.mutate(positionFormData);
    }
  };

  const handleDeletePosition = (position: PositionType) => {
    if (window.confirm(`Are you sure you want to deactivate "${position.name}"?`)) {
      deletePositionMutation.mutate(position.id);
    }
  };

  // Checklist Item handlers
  const handleCloseChecklistDialog = () => {
    setChecklistDialogOpen(false);
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      category: '',
      position_type_id: undefined,
      sort_order: 0,
      is_required: false,
      default_days: undefined,
      is_active: true,
    });
    setError('');
  };

  const handleEditItem = (item: OnboardingChecklistItem) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category || '',
      position_type_id: item.position_type_id,
      sort_order: item.sort_order,
      is_required: item.is_required,
      default_days: item.default_days,
      is_active: item.is_active,
    });
    setChecklistDialogOpen(true);
  };

  const handleSubmitChecklist = () => {
    if (!formData.name) {
      setError('Name is required');
      return;
    }

    if (editingItem) {
      updateChecklistMutation.mutate({ id: editingItem.id, data: formData });
    } else {
      createChecklistMutation.mutate(formData);
    }
  };

  const handleDeleteItem = (item: OnboardingChecklistItem) => {
    if (window.confirm(`Are you sure you want to deactivate "${item.name}"?`)) {
      deleteChecklistMutation.mutate(item.id);
    }
  };

  // Group checklist items by category, filtered by position type
  const groupedItems = React.useMemo(() => {
    if (!checklistData?.items) return {};

    let items = checklistData.items;

    // Filter by position type if selected
    if (positionFilter === 'universal') {
      items = items.filter(item => !item.position_type_id);
    }

    const groups: Record<string, OnboardingChecklistItem[]> = {};
    items.forEach((item) => {
      const category = item.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });
    return groups;
  }, [checklistData?.items, positionFilter]);

  const positionTypes = positionTypesData?.position_types || [];

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/new-hires')}
          sx={{ mb: 2 }}
        >
          Back to New Hires
        </Button>

        <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
          Onboarding Configuration
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab icon={<WorkIcon />} iconPosition="start" label="Position Types" />
            <Tab icon={<ChecklistIcon />} iconPosition="start" label="Checklist Items" />
          </Tabs>
        </Box>

        {/* Position Types Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Position Types
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setPositionDialogOpen(true)}
            >
              Add Position Type
            </Button>
          </Box>

          <Paper sx={{ p: 2, mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showInactivePositions}
                  onChange={(e) => setShowInactivePositions(e.target.checked)}
                />
              }
              label="Show inactive position types"
            />
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Position types allow you to create different onboarding checklists for different roles.
            For example, a "Driver" position type might have CDL verification tasks, while an "Accountant"
            might have software access tasks. Checklist items without a position type are universal and
            apply to all new hires.
          </Typography>

          {positionTypesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell width={100}>Status</TableCell>
                    <TableCell width={120}>Checklist Items</TableCell>
                    <TableCell width={100} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positionTypes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 4 }}>
                          No position types configured yet. Click "Add Position Type" to create your first one.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    positionTypes.map((position) => {
                      const itemCount = checklistData?.items?.filter(i => i.position_type_id === position.id).length || 0;
                      return (
                        <TableRow
                          key={position.id}
                          sx={{
                            bgcolor: !position.is_active ? 'action.disabledBackground' : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Typography
                              fontWeight="medium"
                              sx={{ color: !position.is_active ? 'text.disabled' : 'inherit' }}
                            >
                              {position.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={!position.is_active ? 'text.disabled' : 'text.secondary'}
                            >
                              {position.description || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={position.is_active ? 'Active' : 'Inactive'}
                              size="small"
                              color={position.is_active ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${itemCount} items`}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditPosition(position)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            {position.is_active && (
                              <Tooltip title="Deactivate">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeletePosition(position)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Checklist Items Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Checklist Items
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setChecklistDialogOpen(true)}
            >
              Add Item
            </Button>
          </Box>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Position Type</InputLabel>
                  <Select
                    value={positionFilter}
                    label="Filter by Position Type"
                    onChange={(e) => setPositionFilter(e.target.value as number | 'all' | 'universal')}
                  >
                    <MenuItem value="all">All Position Types</MenuItem>
                    <MenuItem value="universal">Universal Only (No Position Type)</MenuItem>
                    {positionTypes.filter(p => p.is_active).map(pt => (
                      <MenuItem key={pt.id} value={pt.id}>{pt.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactive}
                      onChange={(e) => setShowInactive(e.target.checked)}
                    />
                  }
                  label="Show inactive items"
                />
              </Grid>
            </Grid>
          </Paper>

          {checklistLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {Object.entries(groupedItems).length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No checklist items configured yet. Click "Add Item" to create your first item.
                  </Typography>
                </Paper>
              ) : (
                Object.entries(groupedItems).map(([category, items]) => (
                  <Paper key={category} sx={{ mb: 3 }}>
                    <Box sx={{ p: 2, bgcolor: 'grey.100' }}>
                      <Typography variant="h6">{category}</Typography>
                    </Box>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell width={40}></TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell width={150}>Position Type</TableCell>
                            <TableCell width={100}>Required</TableCell>
                            <TableCell width={120}>Days to Complete</TableCell>
                            <TableCell width={100}>Status</TableCell>
                            <TableCell width={100} align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {items.map((item) => (
                            <TableRow
                              key={item.id}
                              sx={{
                                bgcolor: !item.is_active ? 'action.disabledBackground' : 'inherit',
                              }}
                            >
                              <TableCell>
                                <DragIcon color="action" sx={{ cursor: 'grab' }} />
                              </TableCell>
                              <TableCell>
                                <Typography
                                  sx={{
                                    color: !item.is_active ? 'text.disabled' : 'inherit',
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  color={!item.is_active ? 'text.disabled' : 'text.secondary'}
                                  sx={{
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {item.description || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {item.position_type ? (
                                  <Chip
                                    label={item.position_type.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                ) : (
                                  <Chip
                                    label="Universal"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                {item.is_required ? (
                                  <Chip label="Yes" size="small" color="warning" />
                                ) : (
                                  <Chip label="No" size="small" variant="outlined" />
                                )}
                              </TableCell>
                              <TableCell>
                                {item.default_days ? `${item.default_days} days` : '-'}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={item.is_active ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={item.is_active ? 'success' : 'default'}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditItem(item)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                {item.is_active && (
                                  <Tooltip title="Deactivate">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteItem(item)}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                ))
              )}
            </>
          )}
        </TabPanel>
      </Box>

      {/* Position Type Dialog */}
      <Dialog open={positionDialogOpen} onClose={handleClosePositionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPosition ? 'Edit Position Type' : 'Add Position Type'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={positionFormData.name}
                onChange={(e) => setPositionFormData({ ...positionFormData, name: e.target.value })}
                required
                placeholder="e.g., Driver, Accountant, Sales Rep"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={positionFormData.description}
                onChange={(e) => setPositionFormData({ ...positionFormData, description: e.target.value })}
                placeholder="Optional description of this position type"
              />
            </Grid>
            {editingPosition && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={positionFormData.is_active}
                      onChange={(e) => setPositionFormData({ ...positionFormData, is_active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePositionDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitPosition}
            disabled={createPositionMutation.isLoading || updatePositionMutation.isLoading}
          >
            {editingPosition ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Checklist Item Dialog */}
      <Dialog open={checklistDialogOpen} onClose={handleCloseChecklistDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingItem ? 'Edit Checklist Item' : 'Add Checklist Item'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Position Type</InputLabel>
                <Select
                  value={formData.position_type_id || ''}
                  label="Position Type"
                  onChange={(e) => setFormData({
                    ...formData,
                    position_type_id: e.target.value === '' ? undefined : Number(e.target.value)
                  })}
                >
                  <MenuItem value="">Universal (All Positions)</MenuItem>
                  {positionTypes.filter(p => p.is_active).map(pt => (
                    <MenuItem key={pt.id} value={pt.id}>{pt.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="Paperwork">Paperwork</MenuItem>
                  <MenuItem value="Training">Training</MenuItem>
                  <MenuItem value="Equipment">Equipment</MenuItem>
                  <MenuItem value="IT Setup">IT Setup</MenuItem>
                  <MenuItem value="Safety">Safety</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  {checklistData?.categories?.filter(c => !['Paperwork', 'Training', 'Equipment', 'IT Setup', 'Safety', 'HR'].includes(c)).map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Days to Complete"
                type="number"
                value={formData.default_days || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  default_days: e.target.value ? parseInt(e.target.value) : undefined
                })}
                helperText="From hire date"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Sort Order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({
                  ...formData,
                  sort_order: parseInt(e.target.value) || 0
                })}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_required}
                    onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
                  />
                }
                label="Required"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChecklistDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitChecklist}
            disabled={createChecklistMutation.isLoading || updateChecklistMutation.isLoading}
          >
            {editingItem ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OnboardingConfig;
